/**
 * DSL_ScrollBar
 * Always-visible custom scrollbar for iOS compatibility
 * Fixed size and position - no dynamic scaling
 * Pass horizontal={true} for a horizontal bottom scrollbar
 */

import { useState, useEffect, useCallback } from 'react'

export function DSL_ScrollBar({ scrollContainerRef, className = '', horizontal = false }) {
  const [scrollPercentage, setScrollPercentage] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  const updateScrollbar = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    if (horizontal) {
      const { scrollLeft, scrollWidth, clientWidth } = container
      const isScrollable = scrollWidth > clientWidth
      setIsVisible(isScrollable)
      if (!isScrollable) return
      const maxScroll = scrollWidth - clientWidth
      const scrollRatio = maxScroll > 0 ? scrollLeft / maxScroll : 0
      setScrollPercentage(scrollRatio * 100)
    } else {
      const { scrollTop, scrollHeight, clientHeight } = container
      const isScrollable = scrollHeight > clientHeight
      setIsVisible(isScrollable)
      if (!isScrollable) return
      const maxScroll = scrollHeight - clientHeight
      const scrollRatio = maxScroll > 0 ? scrollTop / maxScroll : 0
      setScrollPercentage(scrollRatio * 100)
    }
  }, [scrollContainerRef, horizontal])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    // Initial calculation with slight delay for mobile
    setTimeout(updateScrollbar, 100)
    updateScrollbar()

    container.addEventListener('scroll', updateScrollbar)

    const resizeObserver = new ResizeObserver(updateScrollbar)
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener('scroll', updateScrollbar)
      resizeObserver.disconnect()
    }
  }, [scrollContainerRef, updateScrollbar])

  if (!isVisible) return null

  if (horizontal) {
    return (
      <div className={`dsl-scrollbar-horizontal ${className}`}>
        <div className="dsl-scrollbar-horizontal-track">
          <div
            className="dsl-scrollbar-horizontal-thumb"
            style={{
              left: `calc(${scrollPercentage}% - ${scrollPercentage * 0.6}px)`
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={`dsl-scrollbar ${className}`}>
      <div className="dsl-scrollbar-track">
        <div
          className="dsl-scrollbar-thumb"
          style={{
            top: `calc(${scrollPercentage}% - ${scrollPercentage * 0.6}px)`
          }}
        />
      </div>
    </div>
  )
}
